import { LABEL_FONT_SIZE, TITLE_FONT_SIZE } from '../../common/constants'
import { roundToDecimal } from '../../common/utils'
import { DeviceSensorData } from '../../hooks/useMultiDeviceReadings'
import { YRange } from '../../utils/compareView'

// 10-color palette for compare mode (assigned in selection order)
export const COMPARE_COLORS = [
  '#6366F1', // Indigo
  '#EC4899', // Pink
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#84CC16', // Lime
]

// Stable identity for a series. A rainfall/flow port can contribute up to three
// series (volume/rate/total), so the sub-metric has to be part of the key or
// they'd share one legend entry and one hide toggle.
export function seriesId(data: DeviceSensorData): string {
  const base = `${data.deviceId}-${data.sensorPort}`
  return data.subType ? `${base}-${data.subType}` : base
}

export interface CompareSeries {
  id: string
  label: string
  color: string
  data: DeviceSensorData
}

/**
 * One entry per selected series, in selection order, so a series keeps its
 * color and label whichever chart it is moved into.
 */
export function buildCompareSeries(deviceSensorData: DeviceSensorData[], isMixed: boolean): CompareSeries[] {
  return deviceSensorData.map((data, index) => {
    const id = seriesId(data)
    const depthSuffix = data.depth !== null ? ` (${data.depth}")` : ''
    const baseLabel = `${data.deviceName} - ${data.sensorPort}${depthSuffix}`
    // In mixed mode, disambiguate series by sensor type + unit
    const label = isMixed
      ? `${baseLabel} · ${data.sensorTypeLabel}${data.unit ? ` (${data.unit})` : ''}`
      : baseLabel

    return {
      id,
      label,
      color: COMPARE_COLORS[index % COMPARE_COLORS.length],
      data,
    }
  })
}

// CanvasJS points carrying the real value + unit, which the tooltip always
// shows (even when the plotted y is normalized).
// Always x-ascending: the synced hover binary-searches these.
export function toDataPoints(data: DeviceSensorData, plotY?: (value: number) => number) {
  const points = data.readings.map(r => {
    const realY = roundToDecimal(r.value, data.precision)
    return { x: r.timestamp * 1000, y: plotY ? plotY(r.value) : realY, realY, unit: data.unit }
  })
  for (let i = 1; i < points.length; i++) {
    if (points[i].x < points[i - 1].x) return points.sort((a, b) => a.x - b.x)
  }
  return points
}

// Y-axis strategy chosen automatically from the number of distinct units.
export type AxisMode = 'single' | 'dual' | 'normalized'

// Normalize a value into 0–100 over [min,max]. Irrometer (reversed) is inverted
// so that "drier" still reads visually the same direction as its real axis.
function normalize(value: number, min: number, max: number, reversed: boolean): number {
  if (max <= min) return 50
  const n = ((value - min) / (max - min)) * 100
  return reversed ? 100 - n : n
}

interface ChartConfigOptions {
  chartType: string
  // Fallback axis title, e.g. "Solar Radiation"
  chartTitle: string
  isMixed: boolean
  // Shared range for single-type compare, so every chart compares by height
  yRange: YRange | null
}

export interface ChartConfig {
  data: any[]
  axisY: any
  axisY2?: any
  axisMode: AxisMode
  caption: string
}

interface UnitGroups {
  keys: string[]
  meta: Record<string, { label: string; unit: string; reversed: boolean }>
}

// Distinct unit groups (keyed by unitCategoryId), in first-seen order, among
// series that have data — this drives a mixed chart's axis strategy.
function unitGroups(series: CompareSeries[], fallbackLabel: string): UnitGroups {
  const keys: string[] = []
  const meta: UnitGroups['meta'] = {}
  for (const { data } of series) {
    if (!data.hasData) continue
    const key = String(data.unitCategoryId)
    if (!meta[key]) {
      keys.push(key)
      meta[key] = { label: data.sensorTypeLabel || fallbackLabel, unit: data.unit, reversed: data.reverseYAxis }
    } else if (data.reverseYAxis) {
      meta[key].reversed = true
    }
  }
  return { keys, meta }
}

/**
 * CanvasJS series + Y axes for one chart's series. Mixed charts pick an axis
 * strategy from their distinct units (one real axis, left/right axes for two,
 * 0–100% scaling for three or more); single-type charts use the shared range.
 * Series without data stay in `data` (empty) so the hover tooltip can say
 * "No data" for them.
 */
export function buildChartConfig(series: CompareSeries[], opts: ChartConfigOptions): ChartConfig {
  const { chartType, chartTitle, isMixed, yRange } = opts

  const { keys: groupKeys, meta: groupMeta } = unitGroups(series, chartTitle)
  const distinct = groupKeys.length
  // One unit gets its real axis, two get left/right real axes, and three or
  // more are scaled 0–100% (split a series out to read its real values).
  const mode: AxisMode = !isMixed ? 'single' : distinct >= 3 ? 'normalized' : distinct === 2 ? 'dual' : 'single'

  // Map each unit group to a CanvasJS axis (primary/secondary) for dual mode
  const primaryKey = groupKeys[0]
  const secondaryKey = groupKeys[1]

  const data = series.map(({ label, color, data: d }) => {
    // Per-series min/max for normalization (over the full fetched range)
    let min = Infinity
    let max = -Infinity
    if (mode === 'normalized') {
      for (const r of d.readings) {
        if (r.value < min) min = r.value
        if (r.value > max) max = r.value
      }
    }

    return {
      name: label,
      type: chartType,
      showInLegend: false, // We use custom legend
      color,
      xValueType: 'dateTime',
      axisYType: mode === 'dual' && String(d.unitCategoryId) === secondaryKey ? 'secondary' : 'primary',
      dataPoints: d.hasData
        ? toDataPoints(
            d,
            mode === 'normalized' ? v => roundToDecimal(normalize(v, min, max, d.reverseYAxis), 2) : undefined
          )
        : [],
      lineThickness: chartType === 'line' ? 2 : undefined,
      fillOpacity: chartType === 'area' || chartType === 'stepArea' ? 0.3 : undefined,
    }
  })

  const axisTitle = (key: string | undefined) => {
    const meta = key ? groupMeta[key] : undefined
    if (!meta) return series.length === 1 && series[0].data.unit ? series[0].data.unit : chartTitle
    return meta.unit ? `${meta.label} (${meta.unit})` : meta.label
  }
  const axisBase = { titleFontSize: TITLE_FONT_SIZE, labelFontSize: LABEL_FONT_SIZE }
  const count = series.length

  if (mode === 'normalized') {
    return {
      data,
      axisMode: mode,
      axisY: { ...axisBase, includeZero: true, minimum: 0, maximum: 100, title: 'Scaled (0–100%)' },
      caption: `${count} sensors scaled 0–100% • hover for real values`,
    }
  }

  if (mode === 'dual') {
    return {
      data,
      axisMode: mode,
      axisY: { ...axisBase, includeZero: false, title: axisTitle(primaryKey), reversed: groupMeta[primaryKey]?.reversed ?? false },
      axisY2: { ...axisBase, includeZero: false, title: axisTitle(secondaryKey), reversed: groupMeta[secondaryKey]?.reversed ?? false },
      caption: '2 units on left/right axes',
    }
  }

  return {
    data,
    axisMode: mode,
    axisY: {
      ...axisBase,
      includeZero: false,
      title: axisTitle(primaryKey),
      reversed: yRange ? yRange.reversed : (groupMeta[primaryKey]?.reversed ?? false),
      ...(yRange ? { minimum: yRange.minimum, maximum: yRange.maximum } : {}),
    },
    caption: '',
  }
}
